import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { FiUpload, FiPlus, FiTrendingUp, FiLogOut, FiUser, FiSearch, FiCheck, FiX, FiAlertCircle, FiTrash2, FiEdit2 } from 'react-icons/fi';
import { onAuthStateChanged, signOut, signInWithPopup,
         signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import { auth, googleProvider } from "@/firebase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Attach the Firebase ID token to every API request (replaces session cookies).
axios.interceptors.request.use(async (config) => {
  const firebaseUser = auth.currentUser;
  if (firebaseUser && config.url?.startsWith(API)) {
    const token = await firebaseUser.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper function to parse lab reference ranges
const parseLabRefRange = (labRefRange) => {
  if (!labRefRange || typeof labRefRange !== 'string') {
    return { lower: null, upper: null };
  }

  // Remove units and extra text (e.g., "3.5-5.5 mmol/L" or "< 41 IU/L" or "Normal: 12-16")
  let cleaned = labRefRange.trim();

  // Remove common prefixes
  cleaned = cleaned.replace(/^(Normal:|Reference:|Ref:)\s*/i, '');

  // Remove units (anything after a space followed by letters)
  cleaned = cleaned.replace(/\s+[a-zA-Z\/]+$/, '');

  // Handle "< X" or "<X" case
  const lessThanMatch = cleaned.match(/^<\s*([0-9.]+)$/);
  if (lessThanMatch) {
    return { lower: null, upper: parseFloat(lessThanMatch[1]) };
  }

  // Handle "> X" or ">X" case
  const greaterThanMatch = cleaned.match(/^>\s*([0-9.]+)$/);
  if (greaterThanMatch) {
    return { lower: parseFloat(greaterThanMatch[1]), upper: null };
  }

  // Handle "X - Y" or "X-Y" case (range)
  const rangeMatch = cleaned.match(/^([0-9.]+)\s*-\s*([0-9.]+)$/);
  if (rangeMatch) {
    return {
      lower: parseFloat(rangeMatch[1]),
      upper: parseFloat(rangeMatch[2])
    };
  }

  // If we can't parse it, return null
  return { lower: null, upper: null };
};

const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fires on login, logout, and initial token restore.
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        // Backend verifies the token and auto-provisions the profile doc.
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) {
        console.error("Auth check failed:", error.response?.status);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      setUser(null);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, checkAuth, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }
  if (!user) {
    // Check if we're already on login page
    if (window.location.pathname === '/login') {
      return children;
    }
    return <Navigate to="/login" />;
  }
  return children;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isRegister, setIsRegister] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleGoogleSignIn = async () => {
    setProcessing(true);
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
      // onAuthStateChanged in AuthProvider picks it up; the effect above redirects.
    } catch (err) {
      console.error("Authentication failed:", err);
      setError(err.message);
      setProcessing(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError(null);
    try {
      if (isRegister) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      console.error("Authentication failed:", err);
      setError(err.code === 'auth/invalid-credential' ? 'Invalid email or password'
             : err.code === 'auth/email-already-in-use' ? 'Email already registered — sign in instead'
             : err.message);
      setProcessing(false);
    }
  };

  if (processing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Processing authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-red-500 to-pink-500 mb-4">
            <span className="text-3xl">🩸</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">BloodBud</h1>
          <p className="text-gray-600">Your intelligent blood test companion</p>
        </div>
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Welcome!</h2>
          <p className="text-gray-600 text-center mb-6">Sign in to start tracking your blood test results</p>

          <button onClick={handleGoogleSignIn}
                  className="w-full flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-2.5 font-medium text-gray-700 hover:bg-gray-50 mb-4">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
            Sign in with Google
          </button>

          <div className="flex items-center my-4">
            <div className="flex-1 border-t border-gray-200"></div>
            <span className="px-3 text-sm text-gray-400">or</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>

          <form onSubmit={handleEmailSubmit} className="space-y-3">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="Email" className="w-full border border-gray-300 rounded-lg px-3 py-2.5" />
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                   placeholder="Password" className="w-full border border-gray-300 rounded-lg px-3 py-2.5" />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="submit"
                    className="w-full bg-indigo-600 text-white rounded-lg py-2.5 font-medium hover:bg-indigo-700">
              {isRegister ? "Create account" : "Sign in"}
            </button>
          </form>
          <p className="text-sm text-center text-gray-500 mt-3">
            {isRegister ? "Already have an account?" : "New here?"}{" "}
            <button type="button" onClick={() => { setIsRegister(!isRegister); setError(null); }}
                    className="text-indigo-600 hover:underline">
              {isRegister ? "Sign in" : "Create an account"}
            </button>
          </p>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-3">Features:</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center"><FiCheck className="text-green-500 mr-2 flex-shrink-0" />Upload PDF or image blood test files</li>
              <li className="flex items-center"><FiCheck className="text-green-500 mr-2 flex-shrink-0" />AI-powered biomarker extraction with Gemini</li>
              <li className="flex items-center"><FiCheck className="text-green-500 mr-2 flex-shrink-0" />Generate analysis reports from raw data</li>
              <li className="flex items-center"><FiCheck className="text-green-500 mr-2 flex-shrink-0" />Track trends over time with charts</li>
              <li className="flex items-center"><FiCheck className="text-green-500 mr-2 flex-shrink-0" />Compare against optimal ranges (age/sex specific)</li>
              <li className="flex items-center"><FiCheck className="text-green-500 mr-2 flex-shrink-0" />30+ biomarkers across 10 test panels</li>
            </ul>
          </div>
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>How it works:</strong> Sign in with Google or create an account with your email. After authentication, you'll be redirected to your dashboard where you can upload blood tests, view analysis reports, and track biomarker trends over time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [recentResults, setRecentResults] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingReportId, setEditingReportId] = useState(null);
  const [editingReportName, setEditingReportName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, resultsRes] = await Promise.all([
        axios.get(`${API}/reports`, { withCredentials: true }),
        axios.get(`${API}/results`, { withCredentials: true })
      ]);

      // Add biomarker count to each report
      const reportsWithCounts = reportsRes.data.map(report => ({
        ...report,
        biomarkerCount: resultsRes.data.filter(result => result.report_id === report.id).length
      }));

      setReports(reportsWithCounts);
      setRecentResults(resultsRes.data.slice(0, 10));
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this blood test? This will delete the uploaded file and all extracted biomarker data. This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/reports/${reportId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Failed to delete report:", error);
      alert("Failed to delete report. Please try again.");
    }
  };

  const handleDeleteResult = async (resultId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this result? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/results/${resultId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Failed to delete result:", error);
      alert("Failed to delete result. Please try again.");
    }
  };

  const handleStartEdit = (report, e) => {
    e.stopPropagation();
    setEditingReportId(report.id);
    setEditingReportName(report.custom_name || `${report.source_type} Report`);
  };

  const handleSaveEdit = async (reportId, e) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/reports/${reportId}`, { custom_name: editingReportName }, { withCredentials: true });
      setEditingReportId(null);
      setEditingReportName('');
      fetchData();
    } catch (error) {
      console.error("Failed to update report name:", error);
      alert("Failed to update report name. Please try again.");
    }
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingReportId(null);
    setEditingReportName('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'optimal': return 'text-green-600 bg-green-50';
      case 'low': return 'text-yellow-600 bg-yellow-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'critical_low':
      case 'critical_high': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'optimal': return 'Optimal';
      case 'low': return 'Low';
      case 'high': return 'High';
      case 'critical_low': return 'Critical Low';
      case 'critical_high': return 'Critical High';
      case 'unknown': return 'No Range';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center"><span className="text-2xl">🩸</span></div>
              <h1 className="text-2xl font-bold text-gray-900">BloodBud</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button onClick={() => setShowProfileModal(true)} className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"><FiUser /><span>{user?.name}</span></button>
              <button onClick={logout} className="flex items-center space-x-2 text-red-600 hover:text-red-700"><FiLogOut /><span>Logout</span></button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <button onClick={() => navigate('/biomarkers')} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition duration-200 text-left group">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200"><FiSearch className="text-blue-600 text-xl" /></div>
              <div><h3 className="font-semibold text-gray-900">All Biomarkers</h3><p className="text-sm text-gray-600">Visual overview</p></div>
            </div>
          </button>
          <button onClick={() => navigate('/upload')} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition duration-200 text-left group">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200"><FiUpload className="text-indigo-600 text-xl" /></div>
              <div><h3 className="font-semibold text-gray-900">Upload Blood Test</h3><p className="text-sm text-gray-600">PDF or Image</p></div>
            </div>
          </button>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Blood Analysis Reports History</h2>
          <p className="text-sm text-gray-600 mb-4">AI-analyzed reports with extracted biomarker data</p>
          {reports.length === 0 ? (
            <p className="text-gray-600">No analysis reports yet.</p>
          ) : (
            <div className="space-y-3">
              {reports.map((report) => (
                <div
                  key={report.id}
                  onClick={() => editingReportId !== report.id && navigate(`/report/${report.id}`)}
                  className={`flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition ${editingReportId !== report.id ? 'cursor-pointer' : ''}`}
                >
                  <div className="flex-1 min-w-0 mr-4">
                    {editingReportId === report.id ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center space-x-2 mb-1">
                          <input
                            type="text"
                            value={editingReportName}
                            onChange={(e) => setEditingReportName(e.target.value)}
                            className="flex-1 px-3 py-1 border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-semibold text-gray-900"
                            autoFocus
                          />
                          <button
                            onClick={(e) => handleSaveEdit(report.id, e)}
                            className="text-green-600 hover:text-green-700 transition"
                            title="Save"
                          >
                            <FiCheck size={20} />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="text-gray-600 hover:text-gray-700 transition"
                            title="Cancel"
                          >
                            <FiX size={20} />
                          </button>
                        </div>
                        <p className="text-sm text-gray-600">
                          {new Date(report.report_date).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{report.custom_name || `Analysis Report - ${new Date(report.test_date || report.report_date).toLocaleDateString()}`}</h3>
                          <button
                            onClick={(e) => handleStartEdit(report, e)}
                            className="text-blue-600 hover:text-blue-700 transition"
                            title="Rename report"
                          >
                            <FiEdit2 size={16} />
                          </button>
                          {report.biomarkerCount > 0 && (
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded font-medium">
                              {report.biomarkerCount} biomarkers
                            </span>
                          )}
                        </div>
                        {report.test_date && (
                          <p className="text-sm font-medium text-gray-700">
                            Test Date: {new Date(report.test_date).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              ...(report.test_date.includes('T') && new Date(report.test_date).getHours() !== 0 ? {
                                hour: '2-digit',
                                minute: '2-digit'
                              } : {})
                            })}
                            {report.lab_name && ` • ${report.lab_name}`}
                          </p>
                        )}
                        {report.requisition_number && (
                          <p className="text-xs text-gray-600">Req #: {report.requisition_number}</p>
                        )}
                        <p className="text-sm text-gray-500">
                          Uploaded: {new Date(report.report_date).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/report/${report.id}`); }}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition"
                    >
                      View Report
                    </button>
                    <button
                      onClick={(e) => handleDeleteReport(report.id, e)}
                      className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition"
                      title="Delete analysis report"
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Blood Test History - Raw Uploads */}
        <div className="bg-white rounded-xl shadow-md p-6 mt-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Blood Test History</h2>
          <p className="text-sm text-gray-600 mb-4">Raw uploaded files from labs in chronological order</p>
          {reports.length === 0 ? (
            <p className="text-gray-600">No blood tests uploaded yet.</p>
          ) : (
            <div className="space-y-3">
              {reports
                .sort((a, b) => new Date(b.report_date) - new Date(a.report_date))
                .map((report) => (
                <div
                  key={`raw-${report.id}`}
                  onClick={() => navigate(`/report/${report.id}`)}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition cursor-pointer"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-1">
                      <h3 className="font-semibold text-gray-900">
                        {report.original_filename || `${report.source_type.toUpperCase()} Upload`}
                      </h3>
                      {report.source_type === 'pdf' && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded font-medium">PDF</span>
                      )}
                      {report.source_type === 'image' && (
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded font-medium">IMAGE</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 space-y-0.5">
                      {report.lab_name && (
                        <p className="font-medium">{report.lab_name}</p>
                      )}
                      {report.test_date && (
                        <p>Test Date: {new Date(report.test_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}</p>
                      )}
                      <p className="text-gray-500">
                        Uploaded: {new Date(report.report_date).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/report/${report.id}`); }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
                    >
                      View File
                    </button>
                    <button
                      onClick={(e) => handleDeleteReport(report.id, e)}
                      className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition"
                      title="Delete file"
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      {showProfileModal && <ProfileModal onClose={() => setShowProfileModal(false)} />}
    </div>
  );
};

const ProfileModal = ({ onClose }) => {
  const { user, setUser } = useAuth();
  const [dateOfBirth, setDateOfBirth] = useState(user?.date_of_birth || '');
  const [sex, setSex] = useState(user?.sex || '');
  const [saving, setSaving] = useState(false);

  const calculateAge = (dob) => {
    if (!dob) return null;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const currentAge = dateOfBirth ? calculateAge(dateOfBirth) : null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/auth/profile`, { date_of_birth: dateOfBirth, sex }, { withCredentials: true });
      setUser({ ...user, date_of_birth: dateOfBirth, sex, age: currentAge });
      onClose();
    } catch (error) {
      console.error("Failed to update profile:", error);
      alert("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Profile Settings</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
            <input 
              type="date" 
              value={dateOfBirth} 
              onChange={(e) => setDateOfBirth(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
            />
            {currentAge && <p className="text-sm text-gray-500 mt-1">Current age: {currentAge} years</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sex</label>
            <select 
              value={sex} 
              onChange={(e) => setSex(e.target.value)} 
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">Select...</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <p className="text-sm text-gray-600">Date of birth and sex are used to determine age-specific optimal ranges for your blood test markers.</p>
        </div>
        <div className="flex space-x-3 mt-6">
          <button onClick={handleSave} disabled={saving || !dateOfBirth || !sex} className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">{saving ? 'Saving...' : 'Save'}</button>
          <button onClick={onClose} className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-300">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const UploadPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extractedResults, setExtractedResults] = useState([]);
  const [reportId, setReportId] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/reports/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setReportId(response.data.report_id);
      setExtractedResults(response.data.extracted_results);

      // Show message if AI extraction failed
      if (response.data.ai_extraction_failed && response.data.message) {
        alert(`${response.data.message}\n\nYour file has been saved. You can view it in Reports or add results from the Biomarkers page.`);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      await axios.post(`${API}/reports/${reportId}/confirm`, extractedResults, { withCredentials: true });
      navigate('/dashboard');
    } catch (error) {
      console.error("Confirm failed:", error);
      alert("Failed to save results. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/dashboard')} className="text-indigo-600 hover:text-indigo-700 mb-6 flex items-center">← Back to Dashboard</button>
        <div className="bg-white rounded-xl shadow-md p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Upload Blood Test</h1>
          <p className="text-sm text-gray-600 mb-6">Upload PDF or image file from your lab</p>
          {!extractedResults.length ? (
            <div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <FiUpload className="text-6xl text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{file ? file.name : 'Drag and drop or click to select a file'}</p>
                <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleFileChange} className="hidden" id="file-upload" />
                <label htmlFor="file-upload" className="inline-block bg-indigo-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-indigo-700 cursor-pointer">Choose File</label>
              </div>
              {file && <button onClick={handleUpload} disabled={uploading} className="w-full mt-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed">{uploading ? 'Processing...' : 'Upload and Extract'}</button>}
            </div>
          ) : (
            <div>
              {extractedResults.length === 0 ? (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">AI Extraction Unavailable</h2>
                  <div className="mb-6 p-6 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <FiAlertCircle className="text-yellow-600 text-2xl mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-gray-800 font-semibold mb-2">AI extraction is currently unavailable</p>
                        <p className="text-gray-700 mb-4">This is usually due to API quota limits. Your file has been saved successfully and you can view it anytime in your Reports.</p>
                        <p className="text-gray-700 font-semibold">What to do next:</p>
                        <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                          <li>Go to Biomarkers and add results directly to any marker</li>
                          <li>Or view your saved PDF in Reports</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-4">
                    <button onClick={() => navigate('/biomarkers')} className="flex-1 bg-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-indigo-700 flex items-center justify-center space-x-2">
                      <FiSearch />
                      <span>Go to Biomarkers</span>
                    </button>
                    <button onClick={() => navigate('/dashboard')} className="flex-1 bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300">
                      Back to Dashboard
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Review Extracted Results</h2>
                  <p className="text-gray-600 mb-6">AI has extracted {extractedResults.length} results. Please review and confirm.</p>
                  <div className="space-y-3 mb-6">
                    {extractedResults.map((result, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-gray-900">{result.marker_name}</h3>
                            {result.is_custom && <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Custom</span>}
                          </div>
                          <p className="font-bold text-gray-900">{result.value} {result.units}</p>
                        </div>
                        {result.lab_ref_range && (
                          <p className="text-xs text-gray-500">Lab Reference Range: {result.lab_ref_range}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex space-x-4">
                    <button onClick={handleConfirm} className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700">Confirm & Save</button>
                    <button onClick={() => { setExtractedResults([]); setReportId(null); setFile(null); }} className="flex-1 bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300">Start Over</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ManualEntryPage = () => {
  const navigate = useNavigate();
  const [markers, setMarkers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [results, setResults] = useState([]);
  const [currentResult, setCurrentResult] = useState({ value: '', units: '', taken_at: new Date().toISOString().split('T')[0], notes: '' });

  useEffect(() => {
    if (searchTerm) fetchMarkers();
  }, [searchTerm]);

  const fetchMarkers = async () => {
    try {
      const response = await axios.get(`${API}/markers`, { params: { search: searchTerm }, withCredentials: true });
      setMarkers(response.data);
    } catch (error) {
      console.error("Failed to fetch markers:", error);
    }
  };

  const handleSelectMarker = (marker) => {
    setSelectedMarker(marker);
    setCurrentResult({ ...currentResult, units: marker.unit_options[0] || '' });
  };

  const handleAddResult = () => {
    if (!selectedMarker || !currentResult.value) return;
    setResults([...results, { marker_code: selectedMarker.marker_code, marker_name: selectedMarker.marker_name, value: parseFloat(currentResult.value), units: currentResult.units, taken_at: new Date(currentResult.taken_at).toISOString(), notes: currentResult.notes }]);
    setSelectedMarker(null);
    setCurrentResult({ value: '', units: '', taken_at: new Date().toISOString().split('T')[0], notes: '' });
    setSearchTerm('');
  };

  const handleSaveAll = async () => {
    try {
      await axios.post(`${API}/reports/manual`, results, { withCredentials: true });
      navigate('/dashboard');
    } catch (error) {
      console.error("Failed to save results:", error);
      alert("Failed to save results. Please try again.");
    }
  };

  const handleRemoveResult = (index) => {
    setResults(results.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/dashboard')} className="text-indigo-600 hover:text-indigo-700 mb-6 flex items-center">← Back to Dashboard</button>
        <div className="bg-white rounded-xl shadow-md p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Manual Entry</h1>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Test Marker</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-3 text-gray-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search for test name (e.g., Cholesterol, Glucose)" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
            </div>
            {searchTerm && markers.length > 0 && !selectedMarker && (
              <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {markers.slice(0, 10).map((marker) => (
                  <button key={marker.id} onClick={() => handleSelectMarker(marker)} className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                    <div className="font-semibold text-gray-900">{marker.marker_name}</div>
                    <div className="text-sm text-gray-600">{marker.panel}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedMarker && (
            <div className="mb-6 p-6 bg-indigo-50 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{selectedMarker.marker_name}</h3>
                <button onClick={() => setSelectedMarker(null)} className="text-gray-400 hover:text-gray-600"><FiX size={20} /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Value</label><input type="number" step="0.01" value={currentResult.value} onChange={(e) => setCurrentResult({ ...currentResult, value: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" placeholder="Enter value" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Units</label><select value={currentResult.units} onChange={(e) => setCurrentResult({ ...currentResult, units: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent">{selectedMarker.unit_options.map((unit) => <option key={unit} value={unit}>{unit}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Test Date</label><input type="date" value={currentResult.taken_at} onChange={(e) => setCurrentResult({ ...currentResult, taken_at: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label><input type="text" value={currentResult.notes} onChange={(e) => setCurrentResult({ ...currentResult, notes: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" placeholder="Add notes" /></div>
              </div>
              <button onClick={handleAddResult} disabled={!currentResult.value} className="mt-4 w-full bg-indigo-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">Add Result</button>
            </div>
          )}
          {results.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Added Results ({results.length})</h3>
              <div className="space-y-2">
                {results.map((result, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div><h4 className="font-semibold text-gray-900">{result.marker_name}</h4><p className="text-sm text-gray-600">{result.value} {result.units} - {new Date(result.taken_at).toLocaleDateString()}</p></div>
                    <button onClick={() => handleRemoveResult(index)} className="text-red-600 hover:text-red-700"><FiX size={20} /></button>
                  </div>
                ))}
              </div>
              <button onClick={handleSaveAll} className="mt-4 w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700">Save All Results</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InsightsSection = ({ reportId }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const pollRef = useRef(null);
  // Tracks which report this section is currently showing, so late-resolving
  // requests for a previous report can never overwrite the current one.
  const activeIdRef = useRef(reportId);

  const fetchLatest = async () => {
    const idAtCall = reportId;
    try {
      const res = await axios.get(`${API}/reports/${idAtCall}/insights`, { withCredentials: true });
      if (activeIdRef.current === idAtCall) setJob(res.data);
      return res.data;
    } catch (e) {
      if (e.response?.status !== 404) console.error("Failed to load insights", e);
      return null;
    }
  };

  useEffect(() => {
    activeIdRef.current = reportId;
    setJob(null);
    setStartError(null);
    fetchLatest();
    return () => {
      activeIdRef.current = null;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [reportId]);

  useEffect(() => {
    const active = job && (job.status === "pending" || job.status === "running");
    if (active && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const j = await fetchLatest();
        // Guard: if the user navigated to another report while this poll was
        // in flight, pollRef may now belong to the new report — leave it alone.
        if (activeIdRef.current !== reportId) return;
        if (j && (j.status === "complete" || j.status === "failed")) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 5000);
    }
  }, [job]);

  const generate = async () => {
    const idAtCall = reportId;
    setStarting(true);
    setStartError(null);
    try {
      await axios.post(`${API}/reports/${idAtCall}/insights`, {}, { withCredentials: true });
      await fetchLatest();
    } catch (e) {
      console.error("Failed to start insights", e);
      if (activeIdRef.current === idAtCall) {
        setStartError("Could not start insight generation — please try again.");
      }
    } finally {
      setStarting(false);
    }
  };

  const running = starting || (job && (job.status === "pending" || job.status === "running"));

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mt-6" data-testid="insights-section">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">AI Insights</h2>
        <button
          onClick={generate}
          disabled={running}
          className="px-4 py-2 rounded-lg font-semibold bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
        >
          {running ? "Generating… this can take a few minutes" :
            job?.status === "complete" ? "Regenerate" : "Generate AI Insights"}
        </button>
      </div>
      {startError && (
        <p className="text-sm text-red-600 mt-3">{startError}</p>
      )}
      {job?.status === "failed" && (
        <p className="text-sm text-red-600 mt-3">Insight generation failed: {job.error}</p>
      )}
      {job?.status === "complete" && (
        <div className="mt-4 text-sm text-gray-800 whitespace-pre-wrap">{job.insight_text}</div>
      )}
      {!job && !startError && (
        <p className="text-sm text-gray-500 mt-3">
          Get a plain-language explanation of this report, generated by BloodBud's medical AI.
        </p>
      )}
    </div>
  );
};

const ReportDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [id]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/${id}`, { withCredentials: true });
      setReportData(response.data);
    } catch (error) {
      console.error("Failed to fetch report:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'optimal': return 'text-green-600 bg-green-50';
      case 'low': return 'text-yellow-600 bg-yellow-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'critical_low':
      case 'critical_high': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'optimal': return 'Optimal';
      case 'low': return 'Low';
      case 'high': return 'High';
      case 'critical_low': return 'Critical Low';
      case 'critical_high': return 'Critical High';
      case 'unknown': return 'No Range';
      default: return 'Unknown';
    }
  };

  const downloadPDF = () => {
    if (!reportData?.report?.file_url) return;
    const link = document.createElement('a');
    link.href = reportData.report.file_url;
    link.download = `report_${new Date(reportData.report.report_date).toLocaleDateString()}.pdf`;
    link.click();
  };

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  if (!reportData) {
    return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center"><p className="text-gray-600">Report not found</p></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/dashboard')} className="text-indigo-600 hover:text-indigo-700 mb-6 flex items-center">← Back to Dashboard</button>
        <div className="bg-white rounded-xl shadow-md p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">Blood Analysis Report</h1>
              <p className="text-sm text-gray-500">Extracted biomarker data from lab test</p>

              {/* Lab and Location */}
              {(reportData.report.lab_name || reportData.report.facility_location) && (
                <p className="text-gray-800 font-medium mt-2">
                  {reportData.report.lab_name}
                  {reportData.report.facility_location && ` • ${reportData.report.facility_location}`}
                </p>
              )}

              {/* Key Dates */}
              <div className="mt-2 space-y-1">
                {reportData.report.test_date && (
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Collection Date:</span> {new Date(reportData.report.test_date).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      ...(reportData.report.test_date.includes('T') && new Date(reportData.report.test_date).getHours() !== 0 ? {
                        hour: '2-digit',
                        minute: '2-digit'
                      } : {})
                    })}
                  </p>
                )}
                {reportData.report.requisition_number && (
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Requisition #:</span> {reportData.report.requisition_number}
                  </p>
                )}
                <p className="text-sm text-gray-500">
                  {reportData.report.source_type.toUpperCase()} File • Uploaded: {new Date(reportData.report.report_date).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                  {reportData.report.original_filename && ` • ${reportData.report.original_filename}`}
                </p>
              </div>
            </div>
            {reportData.report.file_url && (
              <button onClick={downloadPDF} className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 flex items-center space-x-2">
                <span>Download PDF</span>
              </button>
            )}
          </div>

          {/* Test Result Metadata Section */}
          {(reportData.report.patient_name || reportData.report.medical_aid || reportData.report.tests_requested || reportData.report.clinical_data) && (
            <div className="mb-8 bg-blue-50 rounded-lg p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Lab Report Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {reportData.report.patient_name && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Patient</p>
                    <p className="text-sm text-gray-900">{reportData.report.patient_name}</p>
                    {reportData.report.patient_age && <p className="text-xs text-gray-600">Age: {reportData.report.patient_age}</p>}
                    {reportData.report.patient_dob && <p className="text-xs text-gray-600">DOB: {reportData.report.patient_dob}</p>}
                  </div>
                )}
                {reportData.report.patient_id && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Patient ID</p>
                    <p className="text-sm text-gray-900">{reportData.report.patient_id}</p>
                  </div>
                )}
                {reportData.report.medical_aid && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Medical Aid</p>
                    <p className="text-sm text-gray-900">{reportData.report.medical_aid}</p>
                  </div>
                )}
                {reportData.report.received_date && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Received by Lab</p>
                    <p className="text-sm text-gray-900">
                      {new Date(reportData.report.received_date).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}
                {reportData.report.generated_date && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Report Generated</p>
                    <p className="text-sm text-gray-900">
                      {new Date(reportData.report.generated_date).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}
              </div>
              {reportData.report.tests_requested && (
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-1">Tests Requested</p>
                  <p className="text-sm text-gray-900">{reportData.report.tests_requested}</p>
                </div>
              )}
              {reportData.report.clinical_data && (
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-1">Clinical Notes</p>
                  <p className="text-sm text-gray-900">{reportData.report.clinical_data}</p>
                </div>
              )}
            </div>
          )}

          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Biomarker Results ({reportData.results.length})</h2>
            <p className="text-sm text-gray-600 mb-4">Click any biomarker to view trends and history</p>
            <div className="space-y-3">
              {reportData.results.map((result) => (
                <div
                  key={result.id}
                  onClick={() => navigate(`/trends?marker=${result.marker_code}`)}
                  className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2 flex-1">
                      <h3 className="font-semibold text-gray-900">{result.marker_name}</h3>
                      {result.is_custom && <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Custom</span>}
                    </div>
                    <div className="flex items-center space-x-4">
                      <p className="font-bold text-gray-900 text-lg">{result.value} {result.units}</p>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(result.status)}`}>
                        {getStatusLabel(result.status)}
                      </span>
                    </div>
                  </div>
                  {result.lab_ref_range && (
                    <p className="text-sm text-gray-600 mt-1">Lab Reference Range: {result.lab_ref_range}</p>
                  )}
                  {result.notes && (
                    <p className="text-sm text-gray-500 mt-1 italic">Note: {result.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {reportData.report.file_url && (
            <div className="mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Original Lab Document</h2>
              <p className="text-sm text-gray-600 mb-4">Raw test results file from laboratory</p>
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                <iframe
                  src={reportData.report.file_url}
                  className="w-full h-[800px]"
                  title="Lab Test Results PDF"
                />
              </div>
            </div>
          )}
        </div>

        <InsightsSection reportId={id} />
      </div>
    </div>
  );
};

const BiomarkersOverviewPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('my'); // 'my' or 'system'
  const [overviewData, setOverviewData] = useState([]);
  const [systemBiomarkers, setSystemBiomarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPanel, setFilterPanel] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [sortBy, setSortBy] = useState('recent');
  const [panels, setPanels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchOverview();
    fetchSystemBiomarkers();
    fetchPanels();
  }, []);

  const fetchOverview = async () => {
    try {
      const response = await axios.get(`${API}/markers/user`, { withCredentials: true });
      setOverviewData(response.data.biomarkers);
    } catch (error) {
      console.error("Failed to fetch user biomarkers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemBiomarkers = async () => {
    try {
      const response = await axios.get(`${API}/markers/system`, { withCredentials: true });
      setSystemBiomarkers(response.data.biomarkers);
    } catch (error) {
      console.error("Failed to fetch system biomarkers:", error);
    }
  };

  const fetchPanels = async () => {
    try {
      const response = await axios.get(`${API}/panels`, { withCredentials: true });
      setPanels(response.data.panels);
    } catch (error) {
      console.error("Failed to fetch panels:", error);
    }
  };

  const getPanelColor = (panel) => {
    const colors = {
      'Lipids': 'bg-blue-100 text-blue-700',
      'CBC': 'bg-purple-100 text-purple-700',
      'CMP': 'bg-teal-100 text-teal-700',
      'Liver': 'bg-orange-100 text-orange-700',
      'Thyroid': 'bg-pink-100 text-pink-700',
      'Iron': 'bg-amber-100 text-amber-700',
      'Inflammation': 'bg-red-100 text-red-700',
      'Vitamins': 'bg-green-100 text-green-700',
      'Hormones': 'bg-indigo-100 text-indigo-700',
      'Diabetes': 'bg-yellow-100 text-yellow-700'
    };
    return colors[panel] || 'bg-gray-100 text-gray-700';
  };

  const getStatusBorderColor = (status) => {
    switch (status) {
      case 'optimal': return 'border-green-400 hover:border-green-500';
      case 'low': case 'high': return 'border-yellow-400 hover:border-yellow-500';
      case 'critical_low': case 'critical_high': return 'border-red-400 hover:border-red-500';
      default: return 'border-gray-300 hover:border-gray-400';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'optimal': return 'bg-green-100 text-green-700';
      case 'low': return 'bg-yellow-100 text-yellow-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'critical_low': case 'critical_high': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'optimal': return 'Optimal ✓';
      case 'low': return 'Low';
      case 'high': return 'High';
      case 'critical_low': return 'Critical Low';
      case 'critical_high': return 'Critical High';
      default: return 'No Range';
    }
  };

  const getTrendArrow = (sparklineData) => {
    if (sparklineData.length < 2) return null;
    const first = sparklineData[0].value;
    const last = sparklineData[sparklineData.length - 1].value;
    const change = ((last - first) / first) * 100;
    
    if (Math.abs(change) < 5) return '→';
    return change > 0 ? '↑' : '↓';
  };

  const filteredAndSortedData = () => {
    // Use different data based on active tab
    const sourceData = activeTab === 'my' ? overviewData : systemBiomarkers;
    let filtered = [...sourceData];

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(m =>
        m.marker_name.toLowerCase().includes(searchLower) ||
        m.marker_code.toLowerCase().includes(searchLower) ||
        (m.panel && m.panel.toLowerCase().includes(searchLower))
      );
    }

    // Filter by panel
    if (filterPanel !== 'All') {
      filtered = filtered.filter(m => m.panel === filterPanel);
    }

    // Filter by status
    if (filterStatus === 'optimal') {
      filtered = filtered.filter(m => m.status === 'optimal');
    } else if (filterStatus === 'concerning') {
      filtered = filtered.filter(m => ['low', 'high', 'critical_low', 'critical_high'].includes(m.status));
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'recent') {
        return new Date(b.last_tested) - new Date(a.last_tested);
      } else if (sortBy === 'alpha') {
        return a.marker_name.localeCompare(b.marker_name);
      } else if (sortBy === 'status') {
        const statusOrder = { 'critical_low': 0, 'critical_high': 0, 'high': 1, 'low': 1, 'optimal': 2, 'unknown': 3 };
        return statusOrder[a.status] - statusOrder[b.status];
      } else if (sortBy === 'panel') {
        return a.panel.localeCompare(b.panel);
      }
      return 0;
    });

    return filtered;
  };

  const handleCardClick = (markerCode) => {
    navigate(`/trends?marker=${markerCode}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const displayData = filteredAndSortedData();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/dashboard')} className="text-indigo-600 hover:text-indigo-700 mb-6 flex items-center">
          ← Back to Dashboard
        </button>

        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Biomarkers Overview</h1>

          {/* Tabs */}
          <div className="flex space-x-1 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('my')}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'my'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              My Biomarkers
              <span className="ml-2 px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full">
                {overviewData.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('system')}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'system'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              System Library
              <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                {systemBiomarkers.length}
              </span>
            </button>
          </div>

          <p className="text-gray-600 mb-6">
            {activeTab === 'my'
              ? `Tracking ${overviewData.length} biomarkers you've uploaded across ${new Set(overviewData.map(m => m.panel).filter(Boolean)).size} panels`
              : `${systemBiomarkers.filter(m => m.has_data).length} biomarkers with data across all users • ${systemBiomarkers.length} total in library`
            }
          </p>

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <FiSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search biomarkers by name, code, or panel..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filters and Sorting */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Panel</label>
              <select
                value={filterPanel}
                onChange={(e) => setFilterPanel(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="All">All Panels</option>
                {panels.map(panel => <option key={panel} value={panel}>{panel}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="All">All Status</option>
                <option value="optimal">Optimal Only</option>
                <option value="concerning">Concerning Only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="recent">Most Recent</option>
                <option value="alpha">Alphabetical</option>
                <option value="status">Status (Concerning First)</option>
                <option value="panel">Panel Grouping</option>
              </select>
            </div>
          </div>
        </div>

        {/* Biomarker Cards Grid */}
        {displayData.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <p className="text-gray-600 text-lg">
              {activeTab === 'my'
                ? (overviewData.length === 0
                    ? "No biomarkers tracked yet. Upload a blood test or add results manually to get started."
                    : "No biomarkers match your filters.")
                : (systemBiomarkers.length === 0
                    ? "No biomarkers in the system yet."
                    : "No biomarkers match your filters.")
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayData.map((marker) => (
              <div
                key={marker.marker_code}
                onClick={() => handleCardClick(marker.marker_code)}
                className={`bg-white rounded-xl shadow-md p-6 border-2 ${
                  activeTab === 'my' ? getStatusBorderColor(marker.status || marker.latest_status) : 'border-gray-300'
                } cursor-pointer hover:shadow-xl hover:scale-105 transition-all`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-bold text-gray-900 text-lg flex-1">{marker.marker_name}</h3>
                  <div className="flex flex-col items-end gap-1">
                    {marker.panel && (
                      <span className={`px-2 py-1 text-xs rounded-full ${getPanelColor(marker.panel)}`}>
                        {marker.panel}
                      </span>
                    )}
                    {activeTab === 'system' && !marker.has_data && (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                        No data yet
                      </span>
                    )}
                  </div>
                </div>

                {activeTab === 'my' ? (
                  /* My Biomarkers View */
                  <>
                    {/* Latest Value */}
                    <div className="mb-4">
                      <div className="flex items-baseline space-x-2">
                        <p className="text-3xl font-bold text-gray-900">{marker.latest_value}</p>
                        <p className="text-lg text-gray-600">{Array.isArray(marker.units) ? marker.units[0] : marker.units}</p>
                        {marker.sparkline_data && <span className="text-xl">{getTrendArrow(marker.sparkline_data)}</span>}
                      </div>
                      {marker.latest_status && marker.latest_status !== 'unknown' && (
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${getStatusColor(marker.latest_status)}`}>
                          {getStatusLabel(marker.latest_status)}
                        </span>
                      )}
                    </div>

                    {/* Mini Sparkline */}
                    {marker.sparkline_data && marker.sparkline_data.length > 0 && (
                      <div className="mb-4 h-16">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={marker.sparkline_data}>
                            <Line type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Range Information */}
                    <div className="space-y-2 mb-4">
                      {/* Goal Range - Highest Priority */}
                      {marker.custom_range && (
                        <div className="flex items-center justify-between p-2 bg-purple-50 rounded border border-purple-200">
                          <span className="text-xs font-medium text-purple-900">Goal Range</span>
                          <span className="text-sm font-bold text-purple-700">
                            {marker.custom_range.lower} - {marker.custom_range.upper} {marker.custom_range.units}
                          </span>
                        </div>
                      )}

                      {/* Lab Reference Range */}
                      {marker.lab_ref_range && (
                        <div className="flex items-center justify-between p-2 bg-amber-50 rounded border border-amber-200">
                          <span className="text-xs font-medium text-amber-900">Lab Range</span>
                          <span className="text-sm font-bold text-amber-700">{marker.lab_ref_range}</span>
                        </div>
                      )}

                      {/* Optimal Range */}
                      {marker.optimal_range && (
                        <div className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                          <span className="text-xs font-medium text-green-900">Optimal Range</span>
                          <span className="text-sm font-bold text-green-700">
                            {marker.optimal_range.lower} - {marker.optimal_range.upper} {marker.optimal_range.units}
                          </span>
                        </div>
                      )}

                      {/* System Reference Range */}
                      {marker.optimal_range && marker.optimal_range.ref_lower != null && marker.optimal_range.ref_upper != null && (
                        <div className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                          <span className="text-xs font-medium text-blue-900">System Ref</span>
                          <span className="text-sm font-bold text-blue-700">
                            {marker.optimal_range.ref_lower} - {marker.optimal_range.ref_upper} {marker.optimal_range.units}
                          </span>
                        </div>
                      )}

                      {/* No Ranges Available */}
                      {!marker.custom_range && !marker.lab_ref_range && !marker.optimal_range && (
                        <div className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                          <span className="text-xs font-medium text-gray-600">No ranges available</span>
                        </div>
                      )}
                    </div>

                    {/* Footer Info */}
                    <div className="text-sm text-gray-600">
                      <p className="mb-1">Last tested: {new Date(marker.last_measurement).toLocaleDateString()}</p>
                      <div className="flex items-center space-x-2">
                        <p>{marker.measurement_count} measurement{marker.measurement_count !== 1 ? 's' : ''}</p>
                        {marker.is_custom && <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Custom</span>}
                      </div>
                    </div>
                  </>
                ) : (
                  /* System Library View */
                  <div>
                    <div className="mb-4">
                      <p className="text-gray-600">
                        {marker.has_data ? (
                          <span className="text-green-600 font-medium">
                            ✓ {marker.total_measurements} measurement{marker.total_measurements !== 1 ? 's' : ''} across users
                          </span>
                        ) : (
                          <span className="text-gray-500">
                            Available in library - no measurements yet
                          </span>
                        )}
                      </p>
                      {marker.unit_options && (
                        <p className="text-sm text-gray-600 mt-2">
                          Units: {Array.isArray(marker.unit_options) ? marker.unit_options.join(', ') : (Array.isArray(marker.units) ? marker.units.join(', ') : marker.units || 'N/A')}
                        </p>
                      )}
                    </div>
                    {marker.has_data && marker.last_seen && (
                      <p className="text-xs text-gray-500">
                        Last measured: {new Date(marker.last_seen).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TrendsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [markers, setMarkers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [showCustomRangeModal, setShowCustomRangeModal] = useState(false);
  const [customRangeForm, setCustomRangeForm] = useState({
    goal_name: '',
    lower: '',
    upper: ''
  });
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultModalMode, setResultModalMode] = useState('add'); // 'add' or 'edit'
  const [editingResult, setEditingResult] = useState(null);
  const [resultForm, setResultForm] = useState({
    value: '',
    taken_at: new Date().toISOString().slice(0, 16),
    notes: ''
  });

  // Auto-load marker from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const markerCode = params.get('marker');
    if (markerCode && !selectedMarker) {
      loadMarkerByCode(markerCode);
    }
  }, [location.search]);

  useEffect(() => {
    if (searchTerm) fetchMarkers();
  }, [searchTerm]);

  const loadMarkerByCode = async (markerCode) => {
    try {
      // Try to fetch trend data directly - works for both library and custom markers
      const trendRes = await axios.get(`${API}/results/${markerCode}/trend`, { withCredentials: true });

      // Create marker object from trend data
      const marker = trendRes.data.marker || {
        marker_code: markerCode,
        marker_name: trendRes.data.results[0]?.marker_name || markerCode,
        is_custom: markerCode.startsWith('CUSTOM_')
      };

      setSelectedMarker(marker);
      setTrendData(trendRes.data);

      // Pre-fill form if custom range exists
      if (trendRes.data.custom_range) {
        setCustomRangeForm({
          goal_name: trendRes.data.custom_range.goal_name || '',
          lower: trendRes.data.custom_range.lower,
          upper: trendRes.data.custom_range.upper
        });
      } else {
        setCustomRangeForm({ goal_name: '', lower: '', upper: '' });
      }
    } catch (error) {
      console.error("Failed to load marker:", error);
      alert('Failed to load biomarker data. Please try again.');
    }
  };

  const fetchMarkers = async () => {
    try {
      const response = await axios.get(`${API}/markers`, { params: { search: searchTerm }, withCredentials: true });
      setMarkers(response.data);
    } catch (error) {
      console.error("Failed to fetch markers:", error);
    }
  };

  const handleSelectMarker = async (marker) => {
    try {
      const response = await axios.get(`${API}/results/${marker.marker_code}/trend`, { withCredentials: true });
      setSelectedMarker(marker);
      setTrendData(response.data);
      
      // Pre-fill form if custom range exists
      if (response.data.custom_range) {
        setCustomRangeForm({
          goal_name: response.data.custom_range.goal_name || '',
          lower: response.data.custom_range.lower,
          upper: response.data.custom_range.upper
        });
      } else {
        setCustomRangeForm({ goal_name: '', lower: '', upper: '' });
      }
    } catch (error) {
      console.error("Failed to fetch trend:", error);
    }
  };

  const handleSaveCustomRange = async () => {
    if (!customRangeForm.lower || !customRangeForm.upper) {
      alert('Please enter both lower and upper bounds');
      return;
    }

    if (parseFloat(customRangeForm.lower) >= parseFloat(customRangeForm.upper)) {
      alert('Lower bound must be less than upper bound');
      return;
    }

    try {
      await axios.post(`${API}/custom-ranges`, {
        marker_code: selectedMarker.marker_code,
        goal_name: customRangeForm.goal_name || null,
        lower: parseFloat(customRangeForm.lower),
        upper: parseFloat(customRangeForm.upper),
        units: trendData.results[0]?.units || trendData.marker.unit_options[0]
      }, { withCredentials: true });

      // Refresh trend data
      handleSelectMarker(selectedMarker);
      setShowCustomRangeModal(false);
      alert('Custom goal range saved successfully!');
    } catch (error) {
      console.error("Failed to save custom range:", error);
      alert(error.response?.data?.detail || 'Failed to save custom range');
    }
  };

  const handleDeleteCustomRange = async () => {
    if (!confirm('Are you sure you want to delete your custom goal range?')) return;

    try {
      await axios.delete(`${API}/custom-ranges/${selectedMarker.marker_code}`, { withCredentials: true });

      // Refresh trend data
      handleSelectMarker(selectedMarker);
      setShowCustomRangeModal(false);
      setCustomRangeForm({ goal_name: '', lower: '', upper: '' });
      alert('Custom goal range deleted');
    } catch (error) {
      console.error("Failed to delete custom range:", error);
      alert('Failed to delete custom range');
    }
  };

  const handleAddResult = () => {
    setResultModalMode('add');
    setEditingResult(null);
    setResultForm({
      value: '',
      taken_at: new Date().toISOString().slice(0, 16),
      notes: ''
    });
    setShowResultModal(true);
  };

  const handleEditResult = (result) => {
    setResultModalMode('edit');
    setEditingResult(result);
    setResultForm({
      value: result.value,
      taken_at: new Date(result.taken_at).toISOString().slice(0, 16),
      notes: result.notes || ''
    });
    setShowResultModal(true);
  };

  const handleSaveResult = async () => {
    try {
      if (resultModalMode === 'add') {
        // Add new result
        await axios.post(`${API}/results/quick-add`, {
          marker_code: selectedMarker.marker_code,
          value: parseFloat(resultForm.value),
          taken_at: new Date(resultForm.taken_at).toISOString(),
          notes: resultForm.notes || null
        }, { withCredentials: true });
        alert('Result added successfully!');
      } else {
        // Edit existing result
        await axios.put(`${API}/results/${editingResult.id}`, {
          value: parseFloat(resultForm.value),
          taken_at: new Date(resultForm.taken_at).toISOString(),
          notes: resultForm.notes || null
        }, { withCredentials: true });
        alert('Result updated successfully!');
      }

      // Refresh trend data
      const trendRes = await axios.get(`${API}/results/${selectedMarker.marker_code}/trend`, { withCredentials: true });
      setTrendData(trendRes.data);
      setShowResultModal(false);
    } catch (error) {
      console.error("Failed to save result:", error);
      alert(error.response?.data?.detail || 'Failed to save result');
    }
  };

  const handleDeleteResult = async (resultId) => {
    if (!confirm('Are you sure you want to delete this result?')) return;

    try {
      await axios.delete(`${API}/results/${resultId}`, { withCredentials: true });

      // Refresh trend data
      const trendRes = await axios.get(`${API}/results/${selectedMarker.marker_code}/trend`, { withCredentials: true });
      setTrendData(trendRes.data);
      alert('Result deleted');
    } catch (error) {
      console.error("Failed to delete result:", error);
      alert('Failed to delete result');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/biomarkers')} className="text-indigo-600 hover:text-indigo-700 mb-6 flex items-center">← Back to Biomarkers</button>
        <div className="bg-white rounded-xl shadow-md p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Trends & Analysis</h1>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Marker to View Trend</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-3 text-gray-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search for test marker" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
            </div>
            {searchTerm && markers.length > 0 && (
              <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {markers.slice(0, 10).map((marker) => (
                  <button key={marker.id} onClick={() => handleSelectMarker(marker)} className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                    <div className="font-semibold text-gray-900">{marker.marker_name}</div>
                    <div className="text-sm text-gray-600">{marker.panel}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {trendData && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{trendData.marker.marker_name}</h2>
                <p className="text-gray-600">{trendData.marker.education_text}</p>
              </div>
              {trendData.results.length === 0 ? (
                <div>
                  {/* No Data View - Still show ranges and info */}
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
                    <div className="flex items-start">
                      <FiAlertCircle className="text-blue-500 mt-1 mr-3 flex-shrink-0" size={24} />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">No Measurements Yet</h3>
                        <p className="text-gray-700">You haven't uploaded any test results for this marker yet. Upload a blood test or add results manually to start tracking.</p>
                        <button
                          onClick={() => navigate('/upload')}
                          className="mt-3 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700"
                        >
                          Upload Blood Test
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Show Range Information */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Reference Ranges</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* System Reference Range */}
                      {trendData.optimal_range && trendData.optimal_range.ref_lower != null && trendData.optimal_range.ref_upper != null && (
                        <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                          <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                            <span className="w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                            System Reference Range
                          </h3>
                          <p className="text-gray-700 font-bold text-lg">{trendData.optimal_range.ref_lower} - {trendData.optimal_range.ref_upper} {trendData.optimal_range.units}</p>
                          <p className="text-sm text-gray-600 mt-1">Standard clinical reference range</p>
                        </div>
                      )}

                      {/* Optimal Range */}
                      {trendData.optimal_range && (
                        <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                          <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                            <span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                            Optimal Range
                          </h3>
                          <p className="text-gray-700 font-bold text-lg">{trendData.optimal_range.lower} - {trendData.optimal_range.upper} {trendData.optimal_range.units}</p>
                          <p className="text-sm text-gray-600 mt-1">Evidence-based healthy range</p>
                        </div>
                      )}

                      {/* Set Goal Range Card */}
                      <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-200 flex flex-col justify-center items-center cursor-pointer hover:bg-purple-100 transition-colors"
                        onClick={() => setShowCustomRangeModal(true)}>
                        <FiPlus className="text-purple-600 mb-2" size={24} />
                        <p className="font-semibold text-purple-900">Set Goal Range</p>
                        <p className="text-sm text-purple-700 mt-1">Define your personal target</p>
                      </div>
                    </div>
                  </div>

                  {/* Marker Description */}
                  {trendData.marker.description && (
                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">About This Marker</h3>
                      <p className="text-gray-700">{trendData.marker.description}</p>
                    </div>
                  )}

                  {/* Additional Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {trendData.marker.panel && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Panel</h4>
                        <span className={`inline-block px-3 py-1 text-sm rounded-full ${getPanelColor(trendData.marker.panel)}`}>
                          {trendData.marker.panel}
                        </span>
                      </div>
                    )}
                    {trendData.marker.unit_options && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Measurement Units</h4>
                        <p className="text-gray-700">{Array.isArray(trendData.marker.unit_options) ? trendData.marker.unit_options.join(', ') : trendData.marker.unit_options}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex flex-wrap items-center gap-4">
                        {/* Lab Reference Range */}
                        {(() => {
                          const firstResult = trendData.results[0];
                          if (firstResult && firstResult.lab_ref_range) {
                            return (
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-0.5" style={{borderTop: '2px dotted #f59e0b'}}></div>
                                <span className="text-sm text-gray-600">Lab Range</span>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* System Reference Range */}
                        {trendData.optimal_range && trendData.optimal_range.ref_lower != null && trendData.optimal_range.ref_upper != null && (
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-0.5" style={{borderTop: '2px dashed #3b82f6'}}></div>
                            <span className="text-sm text-gray-600">System Ref</span>
                          </div>
                        )}

                        {/* Optimal Range */}
                        {trendData.optimal_range && (
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-0.5" style={{borderTop: '2px dashed #10b981'}}></div>
                            <span className="text-sm text-gray-600">Optimal Range</span>
                          </div>
                        )}

                        {/* Goal Range */}
                        {trendData.custom_range && (
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-0.5" style={{borderTop: '2px dashed #8b5cf6'}}></div>
                            <span className="text-sm text-gray-600">My Goal Range</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => setShowCustomRangeModal(true)}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 shrink-0"
                      >
                        {trendData.custom_range ? 'Edit Goal Range' : 'Set Goal Range'}
                      </button>
                    </div>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={trendData.results.map(r => ({ date: new Date(r.taken_at).toLocaleDateString(), value: r.value, status: r.status }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {/* System Reference Range (blue) */}
                        {trendData.optimal_range && trendData.optimal_range.ref_lower != null && trendData.optimal_range.ref_upper != null && (
                          <>
                            <ReferenceLine y={trendData.optimal_range.ref_lower} stroke="#3b82f6" strokeDasharray="3 3" strokeWidth={1.5} label={{ value: 'Ref Lower', position: 'insideBottomLeft', fill: '#3b82f6', fontSize: 11 }} />
                            <ReferenceLine y={trendData.optimal_range.ref_upper} stroke="#3b82f6" strokeDasharray="3 3" strokeWidth={1.5} label={{ value: 'Ref Upper', position: 'insideTopLeft', fill: '#3b82f6', fontSize: 11 }} />
                          </>
                        )}
                        {/* Optimal Range (green) */}
                        {trendData.optimal_range && (
                          <>
                            <ReferenceLine y={trendData.optimal_range.lower} stroke="#10b981" strokeDasharray="5 5" strokeWidth={2} label={{ value: 'Optimal Lower', position: 'insideBottomLeft', fill: '#10b981', fontSize: 12 }} />
                            <ReferenceLine y={trendData.optimal_range.upper} stroke="#10b981" strokeDasharray="5 5" strokeWidth={2} label={{ value: 'Optimal Upper', position: 'insideTopLeft', fill: '#10b981', fontSize: 12 }} />
                          </>
                        )}
                        {/* Goal Range (purple) */}
                        {trendData.custom_range && (
                          <>
                            <ReferenceLine y={trendData.custom_range.lower} stroke="#8b5cf6" strokeDasharray="5 5" strokeWidth={2} label={{ value: 'Goal Lower', position: 'insideBottomRight', fill: '#8b5cf6', fontSize: 12 }} />
                            <ReferenceLine y={trendData.custom_range.upper} stroke="#8b5cf6" strokeDasharray="5 5" strokeWidth={2} label={{ value: 'Goal Upper', position: 'insideTopRight', fill: '#8b5cf6', fontSize: 12 }} />
                          </>
                        )}
                        {/* Lab Reference Range (orange/yellow) - parsed from first result */}
                        {(() => {
                          const firstResult = trendData.results[0];
                          if (firstResult && firstResult.lab_ref_range) {
                            const parsed = parseLabRefRange(firstResult.lab_ref_range);
                            if (parsed.lower !== null && parsed.upper !== null) {
                              return (
                                <>
                                  <ReferenceLine y={parsed.lower} stroke="#f59e0b" strokeDasharray="2 2" strokeWidth={1.5} label={{ value: 'Lab Lower', position: 'insideBottom', fill: '#f59e0b', fontSize: 11 }} />
                                  <ReferenceLine y={parsed.upper} stroke="#f59e0b" strokeDasharray="2 2" strokeWidth={1.5} label={{ value: 'Lab Upper', position: 'insideTop', fill: '#f59e0b', fontSize: 11 }} />
                                </>
                              );
                            } else if (parsed.upper !== null) {
                              // Handle "< X" case
                              return <ReferenceLine y={parsed.upper} stroke="#f59e0b" strokeDasharray="2 2" strokeWidth={1.5} label={{ value: `Lab < ${parsed.upper}`, position: 'insideTop', fill: '#f59e0b', fontSize: 11 }} />;
                            } else if (parsed.lower !== null) {
                              // Handle "> X" case
                              return <ReferenceLine y={parsed.lower} stroke="#f59e0b" strokeDasharray="2 2" strokeWidth={1.5} label={{ value: `Lab > ${parsed.lower}`, position: 'insideBottom', fill: '#f59e0b', fontSize: 11 }} />;
                            }
                          }
                          return null;
                        })()}
                        <Line type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} dot={{ r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {/* Lab Reference Range */}
                    {(() => {
                      const firstResult = trendData.results[0];
                      if (firstResult && firstResult.lab_ref_range) {
                        return (
                          <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200">
                            <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                              <span className="w-3 h-3 rounded-full bg-amber-500 mr-2"></span>
                              Lab Reference Range
                            </h3>
                            <p className="text-gray-700 font-bold text-lg">{firstResult.lab_ref_range}</p>
                            <p className="text-sm text-gray-600 mt-1">From your uploaded lab report</p>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* System Reference Range */}
                    {trendData.optimal_range && trendData.optimal_range.ref_lower != null && trendData.optimal_range.ref_upper != null && (
                      <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                        <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                          <span className="w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                          System Reference Range
                        </h3>
                        <p className="text-gray-700 font-bold text-lg">{trendData.optimal_range.ref_lower} - {trendData.optimal_range.ref_upper} {trendData.optimal_range.units}</p>
                        <p className="text-sm text-gray-600 mt-1">Standard clinical reference range</p>
                      </div>
                    )}

                    {/* Optimal Range */}
                    {trendData.optimal_range && (
                      <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                        <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                          <span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                          Optimal Range
                        </h3>
                        <p className="text-gray-700 font-bold text-lg">{trendData.optimal_range.lower} - {trendData.optimal_range.upper} {trendData.optimal_range.units}</p>
                        <p className="text-sm text-gray-600 mt-1">Evidence-based healthy range</p>
                      </div>
                    )}

                    {/* Goal Range */}
                    {trendData.custom_range && (
                      <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                        <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                          <span className="w-3 h-3 rounded-full bg-purple-500 mr-2"></span>
                          My Goal Range
                          {trendData.custom_range.goal_name && <span className="ml-2 text-sm text-purple-600">({trendData.custom_range.goal_name})</span>}
                        </h3>
                        <p className="text-gray-700 font-bold text-lg">{trendData.custom_range.lower} - {trendData.custom_range.upper} {trendData.custom_range.units}</p>
                        <p className="text-sm text-gray-600 mt-1">Your personal target</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900">All Results</h3>
                      <button
                        onClick={handleAddResult}
                        className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700"
                      >
                        <FiPlus />
                        <span>Add Result</span>
                      </button>
                    </div>
                    <div className="space-y-2">
                      {trendData.results.map((result) => (
                        <div key={result.id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <p className="text-sm text-gray-600">
                                  {new Date(result.taken_at).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                                {/* Source Badge */}
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  result.source === 'manual_edit' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {result.source === 'manual_edit' ? 'Manual Edit' : 'AI Extracted'}
                                </span>
                              </div>
                              <div className="flex items-center space-x-3">
                                <p className="font-bold text-gray-900 text-lg">{result.value} {result.units}</p>
                                {result.status && result.status !== 'unknown' && (
                                  <span className="px-3 py-1 rounded-full text-sm font-medium" style={{
                                    backgroundColor: (result.status === 'optimal' ? '#10b981' : result.status === 'low' ? '#eab308' : result.status === 'high' ? '#f97316' : '#ef4444') + '20',
                                    color: (result.status === 'optimal' ? '#10b981' : result.status === 'low' ? '#eab308' : result.status === 'high' ? '#f97316' : '#ef4444')
                                  }}>
                                    {result.status}
                                  </span>
                                )}
                              </div>
                              {result.lab_ref_range && (
                                <p className="text-xs text-gray-500 mt-1">Lab Range: {result.lab_ref_range}</p>
                              )}
                              {result.notes && (
                                <p className="text-sm text-gray-600 mt-2 italic">Note: {result.notes}</p>
                              )}
                              {result.original_value && (
                                <p className="text-xs text-gray-500 mt-1">Original value: {result.original_value} {result.units}</p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                onClick={() => handleEditResult(result)}
                                className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                title="Edit result"
                              >
                                <FiEdit2 size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteResult(result.id)}
                                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete result"
                              >
                                <FiTrash2 size={18} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Custom Range Modal */}
      {showCustomRangeModal && trendData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Set Custom Goal Range</h2>
            <p className="text-gray-600 mb-4">
              Set your personal target range for <strong>{trendData.marker.marker_name}</strong>
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Goal Name (Optional)
                </label>
                <input
                  type="text"
                  value={customRangeForm.goal_name}
                  onChange={(e) => setCustomRangeForm({ ...customRangeForm, goal_name: e.target.value })}
                  placeholder="e.g., Athletic Performance, Longevity Target"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lower Bound *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={customRangeForm.lower}
                    onChange={(e) => setCustomRangeForm({ ...customRangeForm, lower: e.target.value })}
                    placeholder="Min"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Upper Bound *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={customRangeForm.upper}
                    onChange={(e) => setCustomRangeForm({ ...customRangeForm, upper: e.target.value })}
                    placeholder="Max"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Units:</strong> {trendData.results[0]?.units || trendData.marker.unit_options[0]}
                </p>
                {trendData.optimal_range && (
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Optimal Range:</strong> {trendData.optimal_range.lower} - {trendData.optimal_range.upper}
                  </p>
                )}
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleSaveCustomRange}
                className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-purple-700"
              >
                Save Goal Range
              </button>
              <button
                onClick={() => setShowCustomRangeModal(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>

            {trendData.custom_range && (
              <button
                onClick={handleDeleteCustomRange}
                className="w-full mt-3 text-red-600 hover:text-red-700 text-sm font-medium"
              >
                Delete Custom Range
              </button>
            )}
          </div>
        </div>
      )}

      {/* Result Add/Edit Modal */}
      {showResultModal && trendData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {resultModalMode === 'add' ? 'Add Result' : 'Edit Result'}
            </h2>
            <p className="text-gray-600 mb-4">
              {resultModalMode === 'add' ? 'Add a new result for' : 'Edit result for'}{' '}
              <strong>{trendData.marker.marker_name}</strong>
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Value *
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    step="0.01"
                    value={resultForm.value}
                    onChange={(e) => setResultForm({ ...resultForm, value: e.target.value })}
                    placeholder="Enter value"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    autoFocus
                  />
                  <span className="text-gray-600 font-medium">
                    {trendData.results[0]?.units || trendData.marker.unit_options?.[0] || ''}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={resultForm.taken_at}
                  onChange={(e) => setResultForm({ ...resultForm, taken_at: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={resultForm.notes}
                  onChange={(e) => setResultForm({ ...resultForm, notes: e.target.value })}
                  placeholder="Add any additional notes..."
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
              </div>

              {resultModalMode === 'edit' && editingResult?.original_value && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Original AI-extracted value:</strong> {editingResult.original_value} {editingResult.units}
                  </p>
                </div>
              )}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleSaveResult}
                disabled={!resultForm.value || !resultForm.taken_at}
                className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {resultModalMode === 'add' ? 'Add Result' : 'Update Result'}
              </button>
              <button
                onClick={() => setShowResultModal(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/biomarkers" element={<ProtectedRoute><BiomarkersOverviewPage /></ProtectedRoute>} />
          <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
          <Route path="/trends" element={<ProtectedRoute><TrendsPage /></ProtectedRoute>} />
          <Route path="/report/:id" element={<ProtectedRoute><ReportDetailsPage /></ProtectedRoute>} />
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
